// The Wiki X-ray — an openwiki-style reading of a scope's agent work.
//
// Three stages, run in the browser against the tenant's own LLM proxy:
//
//   1. SCOUT — a scoped sub-agent (same analytics tools the constitution
//      discovery uses) surveys the scope and PLANS the wiki: which pages are
//      worth writing, and which sessions evidence each. This replaces the
//      scrubber's newest-N sample as the corpus selector — a wiki about "what
//      work happens here" needs coverage the agent chooses, not recency.
//   2. EVIDENCE — deterministic. For each planned page, pull the named
//      sessions' newest bodies (bodies are cumulative, so a session's newest
//      requests carry its whole conversation), dedupe ops by block id, and
//      fold them into a bounded text digest.
//   3. SYNTHESIS — one sub-agent call per page writes the markdown, linking
//      sibling pages as [[slug]] and citing evidence as (trace:PROMPT_ID)
//      links restricted to ids the digest actually offered.
//
// The scout may only cite sessions it SAW in query results; the evidence pull
// then only hydrates sessions the scout named; synthesis may only cite trace
// ids the evidence offered. Each stage narrows the last, so a page can never
// reference work the pipeline didn't read.
//
// SCOPE is enforced mechanically, not by the prompt (scoutScope.js). The
// scout's analytics tool flags queries that never reference the scope's ids;
// the evidence pull CARRIES the run's scope, so a session outside it returns
// no rows and its citation is dropped (disclosed via problems); a steered run
// resolves the steered session set INSIDE the scope. Scope dims the traces
// query cannot express are disclosed rather than silently passed.

import { runSubAgent, runWithConcurrency } from "./subAgent.js";
import {
    analyticsSchemaTool,
    createAnalyticsQueryTool,
    createExpandPreviewTool,
} from "./analyticsTool.js";
import { query } from "./dataClient.js";
import { hydratePrompt } from "./triggers/triggerHarness.js";
import { toolResultText } from "./parseApiRequest.js";
import { capOutput } from "../../xray-factory/src/judge/evidence.js";
import { redactPaths } from "../../xray-factory/src/mine/skeleton.js";
import { evidenceScopeSteps, withScopeGuard } from "./scoutScope.js";

export const WIKI_PAGE_KINDS = ["workflow", "subsystem", "tool", "practice"];

// Bounds. The scout names sessions; these caps bound what the host will
// actually pull regardless of what it names. Evidence cost is dominated by
// body reconstruction, so the binding cap is total hydrated prompts.
export const MAX_PAGES = 10;
export const MAX_SESSIONS_PER_PAGE = 6;
export const MAX_SESSIONS_TOTAL = 24;
export const PROMPTS_PER_SESSION = 6;
const HYDRATE_CONCURRENCY = 4;
const SYNTH_CONCURRENCY = 2;
const DIGEST_CHAR_BUDGET = 9000;
const OP_RESULT_CAP = 320;
const OP_ARG_CAP = 200;
const USER_TEXT_CAP = 240;

export const WIKI_PLAN_SCHEMA = {
    type: "object",
    properties: {
        profile: {
            type: "string",
            description: "2-4 sentences: what agent work in this scope is actually about, in the aggregate. Plain prose, grounded in what you queried.",
        },
        pages: {
            type: "array",
            description: `4-${MAX_PAGES} wiki pages worth writing about this scope. A page is a DURABLE concept a reader would return to — a recurring workflow, a subsystem the work keeps touching, a tool practice — never a one-off task or a single session's story. Ordered most to least central.`,
            items: {
                type: "object",
                properties: {
                    slug: { type: "string", description: "Stable kebab-case identifier derived from the subject (e.g. 'release-verification', 'clickhouse-analytics-queries'). Re-derivable: the same subject should yield the same slug on a future run." },
                    title: { type: "string", description: "The page title, 2-6 words." },
                    kind: { type: "string", description: "workflow | subsystem | tool | practice" },
                    summary: { type: "string", description: "1-2 sentences: what the page covers and why it earns a page in this scope." },
                    sessionIds: {
                        type: "array",
                        items: { type: "string" },
                        description: `3-${MAX_SESSIONS_PER_PAGE} session ids that best evidence this page, copied EXACTLY from your query results. Never invent or truncate an id.`,
                    },
                    sourceClusterId: {
                        type: "string",
                        description: "Exact decimal cluster_id when this page is directly grounded in one named ILO_CLUSTER row; otherwise the empty string. Copy it from query output — never infer it from the title or basis.",
                    },
                    sourceClusterGenerationId: {
                        type: "string",
                        description: "Exact generation_id UUID from the SAME ILO_CLUSTER row as sourceClusterId; otherwise the empty string. A cluster id without its generation id is unusable.",
                    },
                    basis: { type: "string", description: "One line: what you queried and what it showed that makes this page real." },
                },
                required: ["slug", "title", "kind", "summary", "sessionIds", "sourceClusterId", "sourceClusterGenerationId", "basis"],
            },
        },
    },
    required: ["profile", "pages"],
};

const SCOUT_SYSTEM_PROMPT = [
    "You plan a wiki over ONE scope of an organization's captured AI-agent traces. You do not",
    "write the wiki — you decide what pages it needs and which sessions evidence each page.",
    "",
    "A good page is a durable concept: a workflow agents keep performing, a subsystem the work",
    "keeps returning to, a tool or practice with a recognizable shape. A bad page is a one-off",
    "task, a single session narrated, or a category so broad it covers everything ('coding').",
    "",
    "Stay inside the scope you are given. Every query you run must carry the scope's filters and",
    "the given time range. Sample, don't scan: aggregate first (clusters, counts by session),",
    "then read a handful of prompt summaries to confirm what a candidate page is about. Avoid",
    "raw event-body scans — summaries and aggregates are enough to plan from.",
    "",
    "Session ids in your plan must be copied exactly from rows you queried. A page whose",
    "evidence you cannot name is not a page — drop it.",
    "If a page is directly grounded in one named ILO_CLUSTER row, copy BOTH its cluster_id",
    "and generation_id into the structured sourceCluster fields. They must come from the same",
    "row. Otherwise return empty strings for both. Never recover identifiers from basis prose.",
    "",
    "When done call return_result. Do not narrate in prose.",
].join("\n");

// The scout's task: the scope pinned first (it cannot be assumed from creds —
// the sub-agent has no dashboard context), then the survey recipe. Scope and
// steer both ride as INTENT here; the mechanical enforcement lives elsewhere
// (the scope-carrying evidence pull, filterPlanToSessions for the steer).
// The scout is told its citations will be validated so it samples
// accordingly, but nothing depends on it obeying.
export function buildScoutTask({ scopeText, rangeText, steerText = null, scopeEnforced = false }) {
    return [
        `Scope: ${scopeText || "the entire organization"}.`,
        `Time range: ${rangeText || "the dashboard's selected range"}. Apply it to every query.`,
        ...(scopeEnforced ? [
            "Your cited evidence sessions are validated against this scope after you return;",
            "a session with no activity inside the scope is dropped from the wiki. Sample and",
            "cite only sessions you saw in scope-filtered query results.",
        ] : []),
        ...(steerText ? [
            `Steer: the operator narrowed this scope with the filter \`${steerText}\`. Only work`,
            "inside the steered corpus is in scope. Your cited evidence sessions are validated",
            "against the steered session set after you return — a page whose sessions all fall",
            "outside the steer is dropped — so sample and cite from steered work only.",
        ] : []),
        "",
        "Plan the wiki for this scope:",
        "1. Shape of the work — the top work-themes by activity (named clusters where available,",
        "   selecting both cluster_id and generation_id for any cluster used as a page source;",
        "   otherwise group prompt volume by session and read session titles/summaries).",
        "2. For each theme worth a page, confirm its texture with a small sample of prompt",
        "   summaries, and collect the session ids that best show it (prefer deep sessions —",
        "   more prompts — over drive-by ones).",
        "3. Return the profile and the page plan. Prefer fewer, realer pages over coverage.",
    ].join("\n");
}

// The dashboard range is null (→ the analytics default of the last 30 days)
// or { from: ISO, to: ISO } — see analyticsClient.rangeToTimeRange.
export function describeRange(range) {
    if (!range || (!range.from && !range.to)) return "the last 30 days";
    const day = (iso) => String(iso || "").slice(0, 10) || "?";
    return `${day(range.from)} to ${day(range.to)}`;
}

const slugify = (s) =>
    String(s || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);

function normalizeKind(v) {
    const s = String(v || "").trim().toLowerCase();
    return WIKI_PAGE_KINDS.includes(s) ? s : "workflow";
}

const MAX_CLUSTER_ID = 2147483647;
const GENERATION_UUID = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

function normalizeClusterSource(clusterId, generationId) {
    const rawId = String(clusterId ?? "").trim();
    const id = /^\d+$/.test(rawId) ? Number(rawId) : null;
    const generation = String(generationId ?? "").trim().toLowerCase();
    if (!Number.isInteger(id) || id < 0 || id > MAX_CLUSTER_ID || !GENERATION_UUID.test(generation)) {
        return { sourceClusterId: null, sourceClusterGenerationId: null };
    }
    return { sourceClusterId: id, sourceClusterGenerationId: generation };
}

// Normalize the scout's raw plan. Pages without a usable slug get one derived
// from the title; pages without any session evidence are dropped (recorded in
// `dropped`) because stage 2 would have nothing to pull and stage 3 nothing to
// cite — an evidence-free page violates the pipeline's narrowing contract.
export function normalizeWikiPlan(raw) {
    const dropped = [];
    const seen = new Set();
    const pages = [];
    for (const p of Array.isArray(raw?.pages) ? raw.pages : []) {
        const title = String(p?.title || "").trim();
        let slug = slugify(p?.slug) || slugify(title);
        if (!slug || !title) {
            dropped.push({ title: title || "(untitled)", reason: "no usable slug or title" });
            continue;
        }
        const sessionIds = (Array.isArray(p?.sessionIds) ? p.sessionIds : [])
            .map((s) => String(s || "").trim())
            .filter(Boolean)
            .slice(0, MAX_SESSIONS_PER_PAGE);
        if (!sessionIds.length) {
            dropped.push({ title, reason: "no evidence sessions named" });
            continue;
        }
        while (seen.has(slug)) slug = `${slug}-2`;
        seen.add(slug);
        const clusterSource = normalizeClusterSource(p?.sourceClusterId, p?.sourceClusterGenerationId);
        pages.push({
            slug,
            title,
            kind: normalizeKind(p?.kind),
            summary: String(p?.summary || "").trim(),
            basis: String(p?.basis || "").trim() || null,
            ...clusterSource,
            sessionIds,
        });
        if (pages.length >= MAX_PAGES) break;
    }
    return {
        profile: String(raw?.profile || "").trim(),
        pages,
        dropped,
    };
}

export async function runWikiScout({ scopeText, rangeText, scopePath = [], steerText = null, model = null, thinkingLevel = null, signal, onEvent } = {}) {
    const nsKey = "wiki-scout";
    const getNamespaceKey = () => nsKey;
    let scopeWarnings = 0;
    const tools = [
        analyticsSchemaTool,
        withScopeGuard(createAnalyticsQueryTool({ getNamespaceKey }), {
            scopePath,
            onWarning: () => { scopeWarnings += 1; },
        }),
        createExpandPreviewTool({ getNamespaceKey }),
    ];
    const scopeEnforced = Array.isArray(scopePath) && scopePath.length > 0;
    const r = await runSubAgent({
        role: "primary",
        model,
        thinkingLevel,
        signal,
        onEvent,
        tools,
        outputSchema: WIKI_PLAN_SCHEMA,
        systemPrompt: SCOUT_SYSTEM_PROMPT,
        task: buildScoutTask({ scopeText, rangeText, steerText, scopeEnforced }),
    });
    if (r.status !== "done" || !r.result || typeof r.result !== "object") {
        if (r.status === "aborted") return { status: "aborted" };
        return { status: "error", error: r.error || new Error(r.reason || "the scout returned no plan"), lastText: r.lastAssistantText || null };
    }
    const plan = normalizeWikiPlan(r.result);
    if (!plan.pages.length) {
        return { status: "error", error: new Error("the scout planned no evidenced pages"), lastText: null };
    }
    return { status: "done", plan, scopeWarnings };
}

// One compact line per op for the evidence digest. Facets (files, urls,
// commands) beat raw args when present; shell-ish inputs fall back to their
// first command line. Home paths are redacted — the digest reaches a model
// and its output reaches an org-visible page, so operator identities in
// paths must not survive the trip.
export function opLine(block, resultsById) {
    const name = String(block?.name || "tool");
    const input = block?.input;
    const facets = block?.facets || {};
    let arg = null;
    if (Array.isArray(facets.commands) && facets.commands.length) arg = facets.commands[0];
    else if (Array.isArray(facets.files) && facets.files.length) arg = facets.files.slice(0, 3).join(", ");
    else if (Array.isArray(facets.urls) && facets.urls.length) arg = facets.urls[0];
    else if (input && typeof input === "object") {
        const v = input.command || input.cmd || input.file_path || input.path || input.url || input.query || input.prompt;
        if (typeof v === "string" && v.trim()) arg = v.trim().split("\n")[0];
        else arg = JSON.stringify(input);
    }
    let line = `${name}${arg ? `: ${capOutput(redactPaths(String(arg)), OP_ARG_CAP)}` : ""}`;
    const res = block?.id ? resultsById?.get(block.id) : null;
    if (res) {
        const text = capOutput(redactPaths(String(toolResultText(res.content) || "")), OP_RESULT_CAP).trim();
        if (res.isError) line += ` → ERROR${text ? `: ${text}` : ""}`;
        else if (text) line += ` → ${text}`;
    }
    return line;
}

// Fold one session's hydrated bodies into digest lines. Bodies are
// CUMULATIVE — the same block recurs in every later request of its session —
// so ops dedupe by block id before anything is counted or printed.
export function digestSession(session) {
    const resultsById = new Map();
    const opsById = new Map();
    const userTexts = [];
    const seenUser = new Set();
    for (const body of session.bodies) {
        for (const turn of body?.parsed?.turns || []) {
            for (const block of turn?.blocks || []) {
                if (block?.type === "tool_result" && block.tool_use_id) {
                    if (!resultsById.has(block.tool_use_id)) resultsById.set(block.tool_use_id, block);
                } else if (block?.type === "tool_use" && block.id) {
                    if (!opsById.has(block.id)) opsById.set(block.id, block);
                } else if (block?.type === "text" && turn?.role === "user") {
                    const t = String(block.text || "").trim();
                    if (t && !seenUser.has(t)) {
                        seenUser.add(t);
                        userTexts.push(t);
                    }
                }
            }
        }
    }
    const lines = [];
    for (const t of userTexts.slice(0, 4)) {
        lines.push(`  user: ${capOutput(redactPaths(t), USER_TEXT_CAP)}`);
    }
    for (const block of opsById.values()) {
        lines.push(`  op: ${opLine(block, resultsById)}`);
    }
    return {
        sessionId: session.sessionId,
        traceId: session.traceId,
        header: `Session ${session.sessionId}`
            + (session.endpointName ? ` · ${session.endpointName}` : "")
            + (session.promptCount ? ` · ${session.promptCount} prompts in range` : "")
            + (session.traceId ? ` · cite as (trace:${session.traceId})` : ""),
        summaries: session.summaries || [],
        lines,
    };
}

// Assemble one page's evidence text under the char budget. Sessions get an
// even share; a session that underuses its share donates the remainder to
// the ones after it.
export function buildPageDigest(page, sessionDigests) {
    const parts = [];
    const chosen = page.sessionIds
        .map((id) => sessionDigests.get(id))
        .filter(Boolean);
    if (!chosen.length) return null;
    let remaining = DIGEST_CHAR_BUDGET;
    for (let i = 0; i < chosen.length; i++) {
        const share = Math.floor(remaining / (chosen.length - i));
        const d = chosen[i];
        const chunk = [
            d.header,
            ...d.summaries.slice(0, 3).map((s) => `  about: ${capOutput(String(s), 160)}`),
            ...d.lines,
        ].join("\n");
        const clipped = chunk.length > share ? `${chunk.slice(0, share)}\n  …(clipped)` : chunk;
        parts.push(clipped);
        remaining -= clipped.length;
        if (remaining <= 0) break;
    }
    return {
        text: parts.join("\n\n"),
        traceIds: chosen.map((d) => d.traceId).filter(Boolean),
        evidence: chosen
            .filter((d) => d.traceId)
            .map((d) => ({ sessionId: d.sessionId, promptId: d.traceId })),
    };
}

async function mapWithConcurrency(items, limit, fn) {
    const settled = await runWithConcurrency(items.map((it) => () => fn(it)), limit);
    return settled;
}

// Stage 2. Pull the named sessions' rows + newest bodies and digest them.
// Session fetches carry range:null deliberately — a session the scout saw
// inside the range may have started before it, and its cumulative bodies are
// the whole point of hydrating newest-first.
//
// Each pull CARRIES the run's scope (the steps the traces query can express),
// so this stage is also the scope gate: a session the scout named from
// another endpoint returns zero rows here and its citation is dropped. Scope
// dims that can't ride the traces query come back in `unenforcedDims` for the
// caller to disclose.
export async function collectWikiEvidence(plan, {
    scopePath = [],
    range = null,
    canReadSensitivePrompts = false,
    signal = null,
    onProgress = null,
} = {}) {
    const { steps: scopeSteps, dropped: unenforcedDims } = evidenceScopeSteps(scopePath);
    const sessionIds = [];
    const seen = new Set();
    for (const page of plan.pages) {
        for (const id of page.sessionIds) {
            if (!seen.has(id)) {
                seen.add(id);
                sessionIds.push(id);
            }
            if (sessionIds.length >= MAX_SESSIONS_TOTAL) break;
        }
        if (sessionIds.length >= MAX_SESSIONS_TOTAL) break;
    }

    let done = 0;
    const report = () => onProgress?.({ stage: "evidence", done, total: sessionIds.length });
    report();

    const results = await mapWithConcurrency(sessionIds, HYDRATE_CONCURRENCY, async (sessionId) => {
        if (signal?.aborted) throw new Error("aborted");
        const res = await query("getTraces", {
            scopePath: [...scopeSteps, { dim: "sessions", id: sessionId }],
            range: null,
            limit: PROMPTS_PER_SESSION,
            sort: "newest",
            offset: 0,
            canReadSensitivePrompts,
        }, { cache: true });
        const rows = Array.isArray(res) ? res : res?.rows || [];
        // Zero in-scope rows means the citation fails the scope gate (or the
        // session has nothing readable); either way it must not become
        // evidence, so it carries no digest.
        if (!rows.length) {
            done += 1;
            report();
            return { outOfScope: true, sessionId };
        }
        const bodies = [];
        for (const row of rows) {
            if (signal?.aborted) throw new Error("aborted");
            const ev = await hydratePrompt(row.id, { signal });
            if (!ev) continue;
            if (ev.parsedConversation && !ev.partialBody) bodies.push({ promptId: row.id, parsed: ev.parsedConversation });
        }
        done += 1;
        report();
        return digestSession({
            sessionId,
            traceId: rows[0]?.id || null,
            endpointName: rows[0]?.endpointName || null,
            promptCount: rows.total ?? rows.length,
            summaries: rows.map((r) => r.summary).filter(Boolean),
            bodies,
        });
    });

    const digests = new Map();
    const failures = [];
    results.forEach((r, i) => {
        if (r.ok && r.value?.outOfScope) {
            failures.push({ sessionId: sessionIds[i], error: "no traces inside this scope; citation dropped" });
        } else if (r.ok && r.value) {
            digests.set(sessionIds[i], r.value);
        } else {
            failures.push({ sessionId: sessionIds[i], error: r.error?.message || String(r.error || "failed") });
        }
    });
    if (signal?.aborted) throw new Error("aborted");
    return { digests, failures, unenforcedDims };
}

export const WIKI_PAGE_SCHEMA = {
    type: "object",
    properties: {
        markdown: {
            type: "string",
            description: "The page body in markdown: a one-paragraph definition first, then short sections (## headings) as the evidence supports — how the work runs, what it touches, variations, failure modes. Descriptive, never prescriptive. Ground every claim in the evidence; never invent specifics.",
        },
        links: {
            type: "array",
            items: { type: "string" },
            description: "Slugs of OTHER planned pages this page's body references with [[slug]] wikilinks.",
        },
    },
    required: ["markdown"],
};

const SYNTH_SYSTEM_PROMPT = [
    "You write ONE page of an internal wiki about how an organization's AI agents actually work,",
    "from evidence extracted out of captured agent sessions.",
    "",
    "Write descriptively — what IS done, in this scope, on this evidence. Never prescribe, never",
    "generalize past the evidence, never invent tools, paths, or outcomes that are not shown.",
    "Describe practice at the level of the work; never characterize an individual person.",
    "",
    "Linking rules:",
    "  • Link a sibling page with [[slug]] (or [[slug|display text]]) where its subject comes up.",
    "    Only slugs from the provided page list — never invent one.",
    "  • Cite evidence inline as a markdown link whose URL is trace:<id>, e.g.",
    "    [one recent run](trace:abc123). Only ids the evidence block offers — never invent one.",
    "",
    "Keep it tight: 150-400 words of body. The reader is an engineer orienting on this org's",
    "agent work, not an auditor.",
].join("\n");

export function buildSynthTask({ page, plan, digest }) {
    const siblings = plan.pages
        .filter((p) => p.slug !== page.slug)
        .map((p) => `  [[${p.slug}]] — ${p.title}`)
        .join("\n");
    return [
        `Write the wiki page "${page.title}" (kind: ${page.kind}).`,
        `Planned angle: ${page.summary || "(none given)"}`,
        page.basis ? `Scout's basis: ${page.basis}` : null,
        "",
        siblings ? `Sibling pages you may [[link]]:\n${siblings}` : "There are no sibling pages.",
        "",
        "Evidence (each session header names the trace id you may cite):",
        digest.text,
    ].filter((l) => l !== null).join("\n");
}

// Strip citations the evidence never offered and wikilinks to pages that
// were never planned. The model is TOLD both rules; this makes them true.
export function sanitizeWikiMarkdown(markdown, { allowedTraceIds = [], allowedSlugs = [] } = {}) {
    const traceOk = new Set(allowedTraceIds.map(String));
    const slugOk = new Set(allowedSlugs.map(String));
    let out = String(markdown || "");
    out = out.replace(/\[([^\]]*)\]\(trace:([^)\s]+)\)/g, (m, label, id) =>
        traceOk.has(String(id)) ? m : label);
    out = out.replace(/\[\[([^\]|]+)(\|[^\]]*)?\]\]/g, (m, target, label) => {
        const slug = String(target).trim();
        if (slugOk.has(slug)) return m;
        return label ? label.slice(1) : slug;
    });
    return out;
}

export async function synthesizeWikiPage({ page, plan, digest, model = null, signal = null }) {
    const r = await runSubAgent({
        role: "primary",
        model,
        signal,
        tools: [],
        outputSchema: WIKI_PAGE_SCHEMA,
        systemPrompt: SYNTH_SYSTEM_PROMPT,
        task: buildSynthTask({ page, plan, digest }),
    });
    if (r.status !== "done" || !r.result || typeof r.result !== "object" || !String(r.result.markdown || "").trim()) {
        if (r.status === "aborted") return { status: "aborted" };
        return { status: "error", error: r.error || new Error(r.reason || "the writer returned no page") };
    }
    const allowedSlugs = plan.pages.map((p) => p.slug);
    const markdown = sanitizeWikiMarkdown(r.result.markdown, {
        allowedTraceIds: digest.traceIds,
        allowedSlugs,
    });
    const links = (Array.isArray(r.result.links) ? r.result.links : [])
        .map((s) => slugify(s))
        .filter((s) => s && s !== page.slug && allowedSlugs.includes(s));
    return {
        status: "done",
        page: {
            ...page,
            markdown,
            links,
            evidence: digest.evidence,
        },
    };
}

// ── Steer (ephemeral runs) ──────────────────────────────────────────

// Page size of the steered-session resolution; a full page means the steer
// matched more sessions than enforcement can see, disclosed via problems.
export const STEER_WIKI_SESSION_CAP = 2000;

// Mechanical steer enforcement at the evidence boundary: keep only each
// page's evidence sessions inside the steered set, and drop pages left with
// none (into `dropped`, so the run's problems say what the steer excluded).
// This is what makes a steered wiki honest whatever the scout's own queries
// returned — the writer only ever reads sessions that match the steer.
export function filterPlanToSessions(plan, keepIds) {
    const keep = keepIds instanceof Set ? keepIds : new Set((keepIds ?? []).map(String));
    const pages = [];
    const dropped = [...plan.dropped];
    for (const page of plan.pages) {
        const sessionIds = page.sessionIds.filter((id) => keep.has(String(id)));
        if (sessionIds.length) pages.push({ ...page, sessionIds });
        else dropped.push({ title: page.title, reason: "all evidence sessions fall outside the steer" });
    }
    return { ...plan, pages, dropped };
}

// The steered SESSION set: sessions with at least one steered prompt — the
// same resolution the Topic patterns lens uses (the steer is PROMPT-level;
// wiki evidence is session-grain). Resolved INSIDE the run's scope, so the
// plan filter below enforces steer AND scope in one set; an org-wide
// resolution here would keep steered sessions from other endpoints.
async function resolveSteeredSessions({ steer, range, scopePath = [] }) {
    // labels:false is load-bearing: this resolution only needs session IDS,
    // and the sessions label overlay is one query PER SESSION — a full page
    // here would fan out into thousands of requests.
    const res = await query("getDimensionEntries", {
        dimKey: "sessions", parentScopePath: scopePath, range,
        limit: STEER_WIKI_SESSION_CAP, sparkline: false, labels: false, offset: 0, query: steer,
    }, { cache: true });
    const entries = res?.entries ?? [];
    return { ids: new Set(entries.map((e) => String(e.id))), capped: entries.length >= STEER_WIKI_SESSION_CAP };
}

// The whole pipeline. Resolves rather than throws:
//   { status: "done", generation } | { status: "aborted" } | { status: "error", error, stage }
// `generation` = { profile, pages, problems, meta } — `problems` carries
// partial failures (dropped plan pages, sessions that failed to hydrate,
// pages the writer failed) so the panel can say what the wiki does NOT cover.
//
// `steer` (query-language text) narrows the run to the steered sessions.
// A steered generation carries `meta.steered` and is EPHEMERAL BY CONTRACT:
// callers must not applyGeneration() it — a steered run would stale every
// durable page it didn't re-find (it wasn't looking at that corpus), and the
// next unsteered run would stale the steered pages right back.
export async function runWikiGeneration({
    scopeText,
    rangeText,
    scopeKey,
    scopePath = [],
    range = null,
    steer = null,
    model = null,
    thinkingLevel = null,
    canReadSensitivePrompts = false,
    signal = null,
    onProgress = null,
} = {}) {
    const steerText = steer && steer.trim() ? steer.trim() : null;
    let steered = null;
    if (steerText) {
        onProgress?.({ stage: "steer" });
        try {
            steered = await resolveSteeredSessions({ steer: steerText, range, scopePath });
        } catch (e) {
            return { status: "error", stage: "steer", error: e instanceof Error ? e : new Error(String(e)) };
        }
        if (signal?.aborted) return { status: "aborted" };
        if (!steered.ids.size) {
            return { status: "error", stage: "steer", error: new Error("no sessions match the steer in this scope and range") };
        }
    }

    onProgress?.({ stage: "scout" });
    let scout;
    try {
        scout = await runWikiScout({ scopeText, rangeText, scopePath, steerText, model, thinkingLevel, signal });
    } catch (e) {
        return { status: "error", stage: "scout", error: e instanceof Error ? e : new Error(String(e)) };
    }
    if (scout.status === "aborted" || signal?.aborted) return { status: "aborted" };
    if (scout.status !== "done") return { status: "error", stage: "scout", error: scout.error };
    let plan = scout.plan;
    if (steered) {
        plan = filterPlanToSessions(plan, steered.ids);
        if (!plan.pages.length) {
            return { status: "error", stage: "steer", error: new Error("every planned page's evidence fell outside the steer") };
        }
    }

    const problems = plan.dropped.map((d) => `plan: dropped "${d.title}" — ${d.reason}`);
    if (steered?.capped) {
        problems.push(`steer: resolved only the ${STEER_WIKI_SESSION_CAP} most active steered sessions — enforcement may over-drop`);
    }
    if (scout.scopeWarnings) {
        problems.push(`scout: ${scout.scopeWarnings} quer${scout.scopeWarnings === 1 ? "y" : "ies"} did not reference the scope filter (flagged in-run); citations were still validated against the scope`);
    }

    let evidence;
    try {
        evidence = await collectWikiEvidence(plan, { scopePath, range, canReadSensitivePrompts, signal, onProgress });
    } catch (e) {
        if (signal?.aborted) return { status: "aborted" };
        return { status: "error", stage: "evidence", error: e instanceof Error ? e : new Error(String(e)) };
    }
    if (signal?.aborted) return { status: "aborted" };
    for (const f of evidence.failures) problems.push(`evidence: session ${f.sessionId} — ${f.error}`);
    for (const dim of evidence.unenforcedDims || []) {
        problems.push(`scope: the ${dim} filter cannot be applied to evidence pulls; citations were not checked against it`);
    }

    const writable = [];
    for (const page of plan.pages) {
        const digest = buildPageDigest(page, evidence.digests);
        // Pages persist their session list — prune it to the sessions that
        // actually passed the scope gate so a stored page never claims
        // evidence the run dropped.
        const sessionIds = page.sessionIds.filter((id) => evidence.digests.has(id));
        if (digest) writable.push({ page: { ...page, sessionIds }, digest });
        else problems.push(`evidence: no session for "${page.title}" survived — page skipped`);
    }
    if (!writable.length) {
        return { status: "error", stage: "evidence", error: new Error("no planned page kept any evidence") };
    }

    let done = 0;
    onProgress?.({ stage: "pages", done, total: writable.length });
    const settled = await mapWithConcurrency(writable, SYNTH_CONCURRENCY, async ({ page, digest }) => {
        const r = await synthesizeWikiPage({ page, plan, digest, model, signal });
        done += 1;
        onProgress?.({ stage: "pages", done, total: writable.length });
        return r;
    });
    if (signal?.aborted) return { status: "aborted" };

    const pages = [];
    settled.forEach((s, i) => {
        const title = writable[i].page.title;
        if (!s.ok) problems.push(`write: "${title}" — ${s.error?.message || "failed"}`);
        else if (s.value.status === "done") pages.push(s.value.page);
        else if (s.value.status !== "aborted") problems.push(`write: "${title}" — ${s.value.error?.message || "failed"}`);
    });
    if (!pages.length) {
        return { status: "error", stage: "pages", error: new Error("every page write failed"), problems };
    }

    return {
        status: "done",
        generation: {
            profile: plan.profile,
            pages,
            problems,
            meta: {
                scopeKey: scopeKey || null,
                scopeText: scopeText || null,
                rangeText: rangeText || null,
                // Non-null marks the generation EPHEMERAL — see the pipeline
                // doc comment; callers must not persist a steered generation.
                steered: steerText,
                model: model || null,
                at: Date.now(),
                planned: plan.pages.length,
                sessionsPulled: evidence.digests.size,
            },
        },
    };
}
